import { DragEvent, useRef, useState } from "react"
import { t } from "../../i18n"
import {
  importSpreadsheet,
  isSpreadsheetFile,
  SpreadsheetImportError,
  SpreadsheetImportKind,
} from "../../services/spreadsheetImportService"

function addedMessage(kind: SpreadsheetImportKind, count: number) {
  const noun = kind === "employees"
    ? t("import.nounEmployees")
    : kind === "job-codes"
      ? t("import.nounJobCodes")
      : t("import.nounJobSites")
  return t("import.added").replace("{count}", String(count)).replace("{noun}", noun)
}

function sourceMessage(kind: SpreadsheetImportKind) {
  if (kind === "employees") return t("import.sourceEmployees")
  if (kind === "job-codes") return t("import.sourceJobCodes")
  return t("import.sourceJobSites")
}

export default function SpreadsheetImportZone({
  kind,
  managementCompanyId,
  onImported,
}: {
  kind: SpreadsheetImportKind
  managementCompanyId?: number
  onImported?: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState("")
  const [failed, setFailed] = useState(false)

  const handleFile = async (file?: File) => {
    if (!file || !managementCompanyId || uploading) return
    if (!isSpreadsheetFile(file)) {
      setFailed(true)
      setStatus(t("import.invalidType"))
      return
    }

    setUploading(true)
    setFailed(false)
    setStatus(t("import.reading"))
    try {
      const result = await importSpreadsheet({ kind, file, managementCompanyId })
      setStatus(addedMessage(kind, result.imported))
      onImported?.()
    } catch (error) {
      setFailed(true)
      setStatus(
        error instanceof SpreadsheetImportError && error.issue === "columns"
          ? t("import.missingColumns")
          : t("import.failed"),
      )
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ""
    }
  }

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragOver(false)
    void handleFile(event.dataTransfer.files?.[0])
  }

  return (
    <label
      onDragOver={(event) => {
        event.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`mb-6 block cursor-pointer rounded-2xl border-2 bg-secondary-extra-light px-6 py-10 text-center font-source ${
        dragOver ? "border-primary" : "border-primary"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx,.xlsm,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
        className="sr-only"
        disabled={uploading || !managementCompanyId}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
      <p className="text-primary-dark">{t("import.drop")}</p>
      <p className="mt-2 text-sm text-gray-600">{t("import.helper")}</p>
      <p className="mt-2 text-sm text-gray-600">{sourceMessage(kind)}</p>
      {status ? (
        <p className={`mt-3 text-sm ${failed ? "text-red-600" : "text-primary-dark"}`} role="status">
          {status}
        </p>
      ) : null}
    </label>
  )
}
